import { formatSecondsToTimestamp } from "@memorang/helpers/common";
import { useCallback, useEffect, useRef, useState } from "react";

export type UseWritingTimerArgs = {
	timeLimit?: number;
	startTrigger: "mount" | "manual";
	disabled?: boolean;
	initialElapsedSeconds?: number;
	onExpire?: () => void;
};

export type UseWritingTimerResult = {
	start: () => void;
	remainingSeconds: number;
	remainingLabel: string;
	isStarted: boolean;
	isExpired: boolean;
};

const useWritingTimer = ({
	timeLimit,
	startTrigger,
	disabled,
	initialElapsedSeconds = 0,
	onExpire,
}: UseWritingTimerArgs): UseWritingTimerResult => {
	const [remainingSeconds, setRemainingSeconds] = useState<number>(
		timeLimit ?? 0,
	);
	const [isStarted, setIsStarted] = useState(false);
	const startedRef = useRef(false);
	const expiredFiredRef = useRef(false);

	const start = useCallback(() => {
		if (startedRef.current) {
			return;
		}
		if (typeof timeLimit !== "number" || timeLimit <= 0) {
			return;
		}
		startedRef.current = true;
		expiredFiredRef.current = false;
		setRemainingSeconds(Math.max(0, timeLimit - initialElapsedSeconds));
		setIsStarted(true);
	}, [timeLimit, initialElapsedSeconds]);

	useEffect(() => {
		if (startTrigger !== "mount") {
			return;
		}
		if (disabled) {
			return;
		}
		start();
	}, [startTrigger, disabled, start]);

	useEffect(() => {
		if (!isStarted) {
			return;
		}
		if (disabled) {
			return;
		}
		const id = setInterval(() => {
			setRemainingSeconds((prev) => {
				if (prev <= 1) {
					clearInterval(id);
					return 0;
				}
				return prev - 1;
			});
		}, 1000);
		return () => clearInterval(id);
	}, [isStarted, disabled]);

	const remainingLabel = formatSecondsToTimestamp(remainingSeconds, {
		padMinutes: true,
	});
	const isExpired = isStarted && remainingSeconds === 0;

	useEffect(() => {
		if (isExpired && !expiredFiredRef.current) {
			expiredFiredRef.current = true;
			onExpire?.();
		}
	}, [isExpired, onExpire]);

	return {
		start,
		remainingSeconds,
		remainingLabel,
		isStarted,
		isExpired,
	};
};

export default useWritingTimer;
